import React, { createContext, useContext, useEffect, useState } from 'react';
import { User as FirebaseUser, onAuthStateChanged, signOut, signInWithEmailAndPassword, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { auth, database } from '../firebase/config';
import { User } from '../types';

interface AdminAuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutComplete: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

const fetchUserData = async (firebaseUser: FirebaseUser): Promise<User> => {
  try {
    const userRef = ref(database, `users/${firebaseUser.uid}`);
    const snapshot = await get(userRef);
    
    if (snapshot.exists()) {
      const userData = snapshot.val();
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        ...userData
      };
    } else {
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email || ''
      };
    }
  } catch (error) {
    
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email || ''
    };
  }
};

const checkAdminStatus = async (uid: string): Promise<boolean> => {
  try {
    
    const adminRef = ref(database, `admin/${uid}`);
    const snapshot = await get(adminRef);
    
    if (snapshot.exists()) {
      
      return true;
    } else {
      
      return false;
    }
  } catch (error) {
    
    return false;
  }
};

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdminSession, setIsAdminSession] = useState(false);

  const login = async (email: string, password: string) => {
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userData = await fetchUserData(userCredential.user);
    const isAdmin = await checkAdminStatus(userCredential.user.uid);
    
    if (!isAdmin) {
      // Não fazer signOut para não afetar outras sessões (usuário)
      // await signOut(auth);
      throw new Error('Usuário não possui permissões de administrador');
    }
    
    setCurrentUser(userData);
    setIsAdminSession(true);
  };

  const logout = async () => {
    
    // Não fazer signOut para não afetar outras sessões
    // Apenas limpar o estado local deste contexto
    setCurrentUser(null);
    setIsAdminSession(false);
  };

  const logoutComplete = async () => {
    
    // Limpar estado local
    setCurrentUser(null);
    setIsAdminSession(false);
    // Fazer signOut completo do Firebase
    await signOut(auth);
  };

  const refreshUserData = async () => {
    const firebaseUser = auth.currentUser;
    if (firebaseUser && isAdminSession) {
      const userData = await fetchUserData(firebaseUser);
      setCurrentUser(userData);
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('Usuário não autenticado');
    }

    try {
      // Reautenticar o usuário com a senha atual
      const credential = EmailAuthProvider.credential(firebaseUser.email!, currentPassword);
      await reauthenticateWithCredential(firebaseUser, credential);
      
      // Atualizar a senha
      await updatePassword(firebaseUser, newPassword);
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        throw new Error('Senha atual incorreta');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('A nova senha deve ter pelo menos 6 caracteres');
      } else {
        throw new Error('Erro ao alterar senha: ' + error.message);
      }
    }
  };

  useEffect(() => {
    
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      
      
      if (firebaseUser) {
        try {
          const userData = await fetchUserData(firebaseUser);
          const isAdmin = await checkAdminStatus(firebaseUser.uid);
          
          // Só aceitar se for um admin válido
          if (isAdmin) {
            
            setCurrentUser(userData);
            setIsAdminSession(true);
          } else {
            
            // Não limpar a sessão se o usuário pode ser um usuário comum
            // setCurrentUser(null);
            // setIsAdminSession(false);
          }
        } catch (error) {
          
          setCurrentUser(null);
          setIsAdminSession(false);
        }
      } else {
        
        setCurrentUser(null);
        setIsAdminSession(false);
      }
      
      setLoading(false);
    });

    return () => {
      
      unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    loading,
    login,
    logout,
    logoutComplete,
    refreshUserData,
    changePassword
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};